import { useToast } from '@/shared/lib';
import { useDeleteApiToken } from '../api';
import { useTranslation } from 'react-i18next';
import type { ConfirmationConfig } from '@/widgets/confirmation-modal';
import { useCallback } from 'react';
import { useTwoFactorAction } from '@/entities/mfa-identity';
import type { DeleteApiTokenRequestDto } from './types';

type Props = {
  accountNumber: string;
  onDeletionSuccess: () => void;
  openConfirmationModal: (config: ConfirmationConfig) => void;
  onBack: () => void;
};

export const useDeleteApiTokenFlow = ({
  accountNumber,
  onDeletionSuccess,
  openConfirmationModal,
  onBack,
}: Props) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { mutateAsync: deleteToken, isPending: isDeleteApiTokenPending } =
    useDeleteApiToken({ throwOnError: false });

  const { execute } = useTwoFactorAction<void, DeleteApiTokenRequestDto>({
    requestFn: ({ accountNumber, webapiTokenid, twoFA }) =>
      deleteToken({ accountNumber, webapiTokenid, twoFA }),
    onBack,
    onSuccess: () => {
      toast.success(t('webApiTokens.deletion.result.success'));
      onDeletionSuccess();
      onBack();
    },
    onError: () => {
      toast.error(t('webApiTokens.deletion.result.error'));
    },
  });

  const deleteApiToken = useCallback(
    (tokenId: string) => {
      openConfirmationModal({
        title: t('webApiTokens.deletion.title'),
        description: t('webApiTokens.deletion.description'),
        confirmText: t('webApiTokens.deletion.confirmText'),
        cancelText: t('webApiTokens.deletion.cancelText'),
        onConfirm: () =>
          execute({ accountNumber, webapiTokenid: tokenId, twoFA: null }),
      });
    },
    [openConfirmationModal, t, execute]
  );

  return {
    deleteApiToken,
    isDeleteApiTokenPending,
  };
};
